import { Tab, Tabs, alpha, Stack, Container, Typography } from '@mui/material';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useAppSelector } from 'src/store';
import { Pagination } from 'src/type/utils';
import { useBoolean } from 'src/hooks/use-boolean';
import axios, { endpoints } from 'src/utils/axios';
import { fParams } from 'src/utils/format-params';
import { toastError } from 'src/utils/format-error';
import { useInView } from 'react-intersection-observer';
import CenterStack from 'src/components/ui-kit/center-stack';
import { NotificationType } from 'src/type/notification';
import NotificationItem from '../notification-item';
import NotificationSkeleton from '../notification-skeleton';

type NotificationParamsType = {
  isRead: string;
  page: number;
  lastDocId: string;
};

export default function NotificationView() {
  const [scrollTrigger, isInView] = useInView();
  const { notification } = useAppSelector((state) => state.badge);
  const [tableData, setTableData] = useState(Array<NotificationType>());

  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const [hasMoreData, setHasMoreData] = useState(true);

  const initialize = useBoolean(true);

  useEffect(() => {
    load();
  }, [activeTab]);

  useEffect(() => {
    if (isInView && hasMoreData) {
      loadMore();
    }
  }, [isInView, hasMoreData]);

  const load = async () => {
    try {
      initialize.onTrue();
      const data = await getNotification({
        isRead: activeTab,
        page: 1,
        lastDocId: '',
      });
      setTableData(data);
      setCurrentPage(1);
      setHasMoreData(true);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const loadMore = async () => {
    try {
      if (hasMoreData && !initialize.value) {
        const data = await getNotification({
          isRead: activeTab,
          page: currentPage + 1,
          lastDocId: tableData[tableData.length - 1]?.id || '',
        });
        if (data.length === 0) {
          setHasMoreData(false);
        }
        setTableData((prev) => [...prev, ...data]);
        setCurrentPage((prev) => prev + 1);
      }
    } catch (error) {
      toastError(error);
    }
  };

  const getNotification = async (params: NotificationParamsType) => {
    const _params = {
      ...params,
      isRead: params.isRead === 'all' ? undefined : params.isRead !== 'read',
      limit: 30,
    };
    const { data } = await axios.get<Pagination<NotificationType>>(endpoints.notifications.get, {
      params: fParams(_params),
    });
    return data.results;
  };

  const handleRead = useCallback((id: string) => {
    try {
      axios.patch<NotificationType>(endpoints.notifications.read(id));
      setTableData((prev) =>
        prev.map((item) => (item.id === id ? { ...item, isRead: true } : item))
      );
    } catch (error) {
      toastError(error);
    }
  }, []);

  const handleChangeTab = useCallback((tab: string) => {
    setHasMoreData(true);
    setActiveTab(tab);
  }, []);

  const tabs = useMemo(
    () => [
      { value: 'all', label: 'ทั้งหมด' },
      {
        value: 'read',
        label: 'ยังไม่อ่าน',
        count: notification.unReadCount,
      },
      {
        value: 'unread',
        label: 'อ่านแล้ว',
      },
    ],
    [notification]
  );

  return (
    <Container>
      <Stack spacing={2}>
        <Typography variant="h4" gutterBottom>
          การแจ้งเตือน
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_e, newValue) => handleChangeTab(newValue)}
          sx={{
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={`${tab.label} ${'count' in tab ? `(${tab.count})` : ''}`}
            />
          ))}
        </Tabs>

        <Stack spacing={2}>
          {/* <Stack direction="row" justifyContent="flex-end" sx={{ cursor: 'pointer' }}>
            <Link variant="caption">อ่านทั้งหมด</Link>
          </Stack> */}
          <NotificationSkeleton loading={initialize.value} rows={20}>
            {tableData.map((item) => (
              <NotificationItem
                key={item?.id}
                title={item.title}
                category={item.category}
                createdAt={item.createdAt}
                isRead={item.isRead}
                link={item.link}
                onRead={() => handleRead(item.id)}
              />
            ))}
          </NotificationSkeleton>

          {hasMoreData ? (
            <div ref={scrollTrigger}>
              <Stack spacing={2}>
                <NotificationSkeleton loading rows={2} />
              </Stack>
            </div>
          ) : (
            <CenterStack mt={3}>
              <Typography variant="caption" color="text.secondary">
                ทั้งหมด {tableData.length} รายการ
              </Typography>
            </CenterStack>
          )}
        </Stack>
      </Stack>
    </Container>
  );
}
